// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The one place an idempotency key is built.
//
// **Moved out of `client.ts` so the front door can mint one** (M13-20, 20 Sep 2026). The headset's
// Keep runs from `src/xr`, which is on the anonymous side of the FR-030 boundary and may not name a
// module that can open a connection — and `client.ts` is that module. The key builder itself opens
// nothing; it is a string function, and this file has no imports at all, so `check-anonymous.mjs`
// walks into it and out again. `client.ts` re-exports it, and every existing `import {
// idempotencyKey } from '@src/net/client'` still reads the same function.

/**
 * An idempotency key the dispatcher will accept.
 *
 * **The contract's key alphabet is `[A-Za-z0-9_-]`, 8 to 128 characters, and seven call sites in
 * this client did not obey it.** They built keys by concatenation — `'delete:' + handle`,
 * `'version:' + id + ':' + semver`, `'checkout:' + profileId + ':' + plan` — and a colon is not in
 * that alphabet. Neither is the dot in a semver, nor the space in a machine title. Every one of
 * those requests was refused by `requestMetaProblem` *before it reached its handler*, with
 * `bad_request` and the client's generic "Something about that request was not right."
 *
 * What that cost, until an armed end-to-end run found it: **a player could not delete their
 * account, could not open the billing portal, could not buy Pro, and could not create, save, ticket
 * or publish a machine.** Seven mutations, one shape of mistake, and a message that named none of
 * them. The keys the *headset* mints were fine throughout — `PinballApi.cs` and
 * `DiagnosticsSender.cs` both quote the pattern in their comments and stay inside it — so this was
 * a browser-only defect in code that reads perfectly.
 *
 * So keys are built here and nowhere else. Anything outside the alphabet becomes a hyphen, runs of
 * hyphens collapse, and the result is padded to the floor rather than throwing: the parts are
 * supplied by the product rather than by a player, so a short key is an oversight in a caller and
 * not a reason to refuse the act it identifies.
 *
 * `test/net/idempotency.test.ts` holds both halves — that this function cannot produce an invalid
 * key, and that no call site builds one any other way.
 */
export function idempotencyKey(...parts: readonly (string | number)[]): string {
  const joined = parts.map((part) => String(part)).join('-')
  const safe = joined.replace(/[^A-Za-z0-9_-]+/gu, '-').replace(/-{2,}/gu, '-')
  const trimmed = safe.replace(/^-+|-+$/gu, '')
  return (trimmed.length >= 8 ? trimmed : `${trimmed}-idempotent`).slice(0, 128)
}
